/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Trash2, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import apiClient from "../../api/client";
import ConfirmModal from "../model/ConfirmModal";
import { uploadMp3Chunks } from "../../utils/uploadMp3Chunks";

type Track = {
  id: string;
  title: string;
  durationText: string;
  filePath: string;
};

interface SongFormProps {
  scheduleId: string;
  onClose?: () => void;
}

export default function SongForm({ scheduleId }: SongFormProps) {
  const [track, setTrack] = useState<Track | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [durationText, setDurationText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(0);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");

  const [errors, setErrors] = useState<{
    title?: string;
    durationText?: string;
    file?: string;
  }>({});

  const [errors2, setErrors2] = useState<{title?: string;}>({});
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ✅ Validation
  const validate = () => {
    const newErrors: typeof errors = {};

    if (!title.trim()) newErrors.title = "Song title is required";

    // if (!durationText.trim()) {
    //   newErrors.durationText = "Song duration is required";
    // } else {
    //   const validDuration = /^\d+:(?:[0-5]\d)$/.test(durationText);
    //   if (!validDuration) newErrors.durationText = "Add valid song duration";
    // }

    // if (!file && !track) newErrors.file = "MP3 file is required";
    if (!file) newErrors.file = "MP3 file is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Fetch existing track
  const fetchTrack = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/schedules/${scheduleId}/tracks`);

      if (data.success && data.data.length > 0) {
        setTrack(data.data[0]);
      } else {
        setTrack(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrack();
  }, [scheduleId]);

  // const handleUpload = async () => {
  //   cancelEdit()
  //   if (!validate() || !file) return;

  //   const formData = new FormData();
  //   formData.append("mp3", file);
  //   formData.append("title", title);
  //   // formData.append("durationText", durationText);

  //   setUploading(true);
  //   setLoading(true);

  //   try {
  //     const { data } = await apiClient.post(
  //       `/stream/upload/${scheduleId}`,
  //       formData,
  //       { headers: { "Content-Type": "multipart/form-data" } }
  //     );

  //     if (data.success) {
  //       toast.success(track ? "MP3 replaced" : "MP3 uploaded");
  //       setFile(null);
  //       setTitle("");
  //       setDurationText("");
  //       fetchTrack();
  //     }
  //   } catch {
  //     toast.error("Upload failed");
  //   } finally {
  //     setUploading(false);
  //     setLoading(false);
  //   }
  // };

  const handleUpload = async () => {
    cancelEdit();
    if (!validate() || !file) return;

    setUploading(true);
    setLoading(true);

    try{
      const filePath = await uploadMp3Chunks(file, setUploadProgress);

      const {data} = await apiClient.post(`/stream/song-meta/${scheduleId}`, {
        title,
        filePath
      })

      if(data?.success){
        toast.success(track ? "MP3 replaced" : "MP3 uploaded");
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setTitle("");
        setDurationText("");
        setUploadProgress(0);
        await fetchTrack();
      }
      else{
        toast.error(data?.message || "Upload failed")
      }
    }
    catch (error: any) {
      // toast.error("Upload failed");
      toast.error(error?.response?.data?.message || error?.message || error || "Upload failed");
      console.log(error)
    }
    finally {
      setUploading(false);
      setLoading(false);
    }
  };


  const handleDelete = async () => {
    if (!track) return;

    try {
      const { data } = await apiClient.patch(`/stream/remove/${track.id}`);

      if (data.success) {
        toast.success("MP3 removed");
        setTrack(null);
      }
    } catch {
      toast.error("Failed to remove MP3");
    }
  };

  const deletePopup=()=>{
    setDeleteOpen(true);
  }

  const startEdit = (track: Track) => {
    setEditingId(track.id);
    setEditTitle(track.title);
    setErrors({})
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setErrors2({})
  };

  const saveEdit = async (id: string) => {
    const newErrors: typeof errors = {};
    if (!editTitle.trim()) newErrors.title = "Song title is required";
    setErrors2(newErrors);
    const validate = Object.keys(newErrors).length === 0;

    if(!validate) return;

    try {
      const {data} = await apiClient.patch(`/stream/update/${id}`, { title: editTitle });
      if(data?.success){
        toast.success("Title updated");
        setEditingId(null);
        fetchTrack();
        setErrors2({})
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || "Update failed");
    }
  };

  return (
    <div className="space-y-6">

      {/* ================= Upload Card ================= */}

      <div className="border border-white/10 rounded-2xl bg-[#0B1623]">
        <div className="px-5 py-4 border-b border-white/10">
          <h3 className="text-sm font-semibold text-white">
            Add mp3 file to schedule
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Upload an MP3 file that will play during this schedule
          </p>
        </div>

        <div className="p-5 space-y-4">

          {/* Title */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              placeholder="Eg. Morning Jazz Intro"
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white"
            />
            {errors.title && (
              <p className="text-xs text-red-500">{errors.title}</p>
            )}
          </div>

          {/* Duration */}
          <div className="hidden">
            <label className="block text-xs text-slate-400 mb-1">
              Duration
            </label>
            <input
              type="text"
              value={durationText}
              placeholder="Eg. 2:30"
              onChange={(e) => setDurationText(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white"
            />
            {errors.durationText && (
              <p className="text-xs text-red-500">{errors.durationText}</p>
            )}
          </div>

          {/* File Input */}
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              MP3 file
            </label>

            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/mpeg"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />

              <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-dashed border-white/15 bg-black/20">
                <span className="text-sm text-slate-300 truncate">
                  {file ? file.name : "Choose MP3 file"}
                </span>
                <Upload className="w-4 h-4 text-slate-400 shrink-0" />
              </div>
            </div>

            {errors.file && (
              <p className="text-xs text-red-500">{errors.file}</p>
            )}
          </div>

          {/* <div className="flex justify-between pt-2 gap-3">
            {uploading && (
              <div className="w-full mt-3">
                <div className="w-full h-3 bg-gray-200 rounded">
                  <div
                    className="h-3 bg-green-600 rounded transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>

                <div className="text-sm mt-1 text-center">
                  {uploadProgress}%
                </div>
              </div>
            )}
            <Button
              onClick={handleUpload}
              disabled={uploading || loading}
              // className="gap-2 min-w-[120px] text-white"
              className="gap-2 min-w-[120px] bg-cyan-600 hover:bg-cyan-700 active:bg-cyan-800 text-white border border-cyan-500 shadow-sm transition"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {track ? "Replace" : "Upload"}
            </Button>
          </div> */}
          <div className="pt-3 space-y-3">

  {/* Progress Section */}
  {uploading && (
    <div className="w-full space-y-2">
      <div className="flex justify-between text-sm font-medium text-white">
        <span>Uploading...</span>
        <span>{uploadProgress}%</span>
      </div>

      <div className="w-full h-3 bg-gray-200 rounded overflow-hidden">
        <div
          className="h-3 bg-green-600 transition-all duration-300"
          style={{ width: `${uploadProgress}%` }}
        />
      </div>
    </div>
  )}

  {/* Upload Button */}
  <Button
    onClick={handleUpload}
    disabled={uploading || loading}
    className="w-full gap-2 min-h-11 bg-cyan-600 hover:bg-cyan-700 active:bg-cyan-800 text-white border border-cyan-500 shadow-sm transition"
  >
    {uploading ? (
      <Loader2 className="w-4 h-4 animate-spin" />
    ) : (
      <Upload className="w-4 h-4" />
    )}
    {uploading ? `${uploadProgress}%` : track ? "Replace" : "Upload"}
  </Button>

</div>

        </div>
      </div>

      {/* ================= Existing Track ================= */}

      {/* <div className="border border-white/10 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 bg-white/5 text-sm font-semibold text-white">
          Songs in this Schedule
        </div>

        {loading ? (
          <div className="p-6 text-center text-slate-400">
            Loading songs...
          </div>
        ) : !track ? (
          <div className="p-6 text-center text-slate-400">
            No song added yet
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            <li className="flex items-center justify-between px-5 py-3 hover:bg-white/5">

              <div>
                <div className="text-white text-sm font-medium">
                  {track.title}
                </div>
                <div className="text-xs text-slate-400">
                  {track.durationText}
                </div>

                <audio
                  controls
                  className="mt-2"
                  src={`${import.meta.env.VITE_API_BASE_URL}/stream/schedule/${scheduleId}`}
                />
              </div>

              <Button
                size="icon"
                variant="ghost"
                onClick={handleDelete}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </li>
          </ul>
        )}
      </div> */}

      <div className="border border-white/10 rounded-2xl overflow-hidden bg-white/[0.02] backdrop-blur-sm">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm tracking-wide">
            MP3 in this Schedule
          </h3>
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            Loading songs...
          </div>
        ) : !track ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            No MP3 file added yet
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            <li className="px-6 py-5 hover:bg-white/5 transition-colors">

              <div className="flex items-start justify-between gap-4">

                {/* Left Section */}
                <div className="flex-1 min-w-0">

                  {/* Title */}
                  {/* <p className="text-white font-medium text-sm truncate">
                    Title : {track.title}
                  </p> */}

                  {/* Title + Edit */}
                  <div className="flex items-center gap-2 max-w-full">

                    {editingId === track.id ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-2">
                          <input
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="bg-white/10 border border-white/20 rounded px-2 py-1 text-sm text-white w-56"
                            autoFocus
                          />

                          <button
                            onClick={() => saveEdit(track.id)}
                            className="text-xs px-4 py-1 bg-cyan-600 text-white rounded hover:bg-cyan-500/60 cursor-pointer"
                          >
                            Save
                          </button>

                          <button
                            onClick={cancelEdit}
                            className="text-xs px-4 py-1 text-white bg-gray-600/80 rounded hover:bg-gray-600 cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                        <div>
                          {errors2.title && (
                            <p className="text-xs text-red-500">{errors2.title}</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-white font-medium text-sm truncate max-w-[260px]">
                          Title : {track.title}
                        </p>

                        <button
                          onClick={() => startEdit(track)}
                          className="
                            text-xs px-2 py-1 bg-white/10 border border-white/20 rounded hover:bg-white/20 shrink-0
                            text-white flex gap-1 cursor-pointer
                          "
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                      </>
                    )}

                  </div>

                  {/* Duration */}
                  {/* <p className="text-xs text-slate-400 mt-1">
                    Duration: {track.durationText}
                  </p> */}

                  {/* Audio Player */}
                  <audio
                    controls
                    controlsList="nodownload"
                    onContextMenu={(e) => e.preventDefault()}
                    className="mt-3 w-full"
                    src={`${import.meta.env.VITE_API_BASE_URL}/stream/schedule/${scheduleId}`}
                  />
                </div>

                {/* Delete Button */}
                <Button
                  size="icon"
                  variant="ghost"
                  // onClick={handleDelete}
                  onClick={deletePopup}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 transition"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>

                <ConfirmModal
                  isOpen={deleteOpen}
                  onClose={() => setDeleteOpen(false)}
                  onConfirm={handleDelete}
                  title="Delete Audio"
                  message="Are you sure you want to delete this audio file permanently?"
                  confirmText="Delete"
                  cancelText="Cancel"
                  isDestructive={true}
                />

              </div>

            </li>
          </ul>
        )}
      </div>

    </div>
  );
}
